import React from "react";
import { Card, CardContent, Typography, Box, Input, Select, Option, Divider } from "@mui/joy";
import { Landmark } from "lucide-react";
import { SettingField, ESTIMATING_CONFIG } from "./EstimatingCommon";

interface FinancialSettingsProps {
    values: Record<string, any>;
    onChange: (key: string, value: any) => void;
}

export default function FinancialSettings({ values, onChange }: FinancialSettingsProps) {
    return (
        <Card variant="soft" sx={{ border: "1px solid", borderColor: "divider" }}>
            <CardContent>
                <Typography level="title-sm" mb={2} startDecorator={<Landmark size={18} />}>
                    Tax, Overhead & Fees
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <SettingField label="Payment Type">
                        <Select
                            size="sm"
                            value={values.EST_PAYMENT_TYPE}
                            onChange={(_, val) => {
                                onChange("EST_PAYMENT_TYPE", val);
                                // Prepopulate payment terms based on selected type
                                let terms = "";
                                if (val === "insurance") {
                                    terms = `Insurance claim — O&P ${values.EST_OP_PERCENT || "20"}%`;
                                } else if (val === "financing") {
                                    terms = "Financing per lender agreement";
                                } else if (val === "credit") {
                                    terms = `Credit card — ${values.EST_CC_FEE_PERCENT || "3.0"}% processing fee included`;
                                } else if (val === "debit") {
                                    terms = "Debit card at project completion";
                                } else if (val === "cash" || val === "cash/check") {
                                    terms = "50% at project start; balance due upon completion";
                                }
                                if (terms) {
                                    onChange("EST_PAYMENT_TERMS", terms);
                                }
                            }}
                            sx={{ minWidth: 140 }}
                        >
                            {ESTIMATING_CONFIG.OPTIONS.PAYMENT_TYPES.map((opt) => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </SettingField>

                    <SettingField label="Payment Terms" description="Terms details printed on the contract PDF">
                        <Input
                            size="sm"
                            placeholder="Enter custom terms..."
                            value={values.EST_PAYMENT_TERMS || ""}
                            onChange={(e) => onChange("EST_PAYMENT_TERMS", e.target.value)}
                            sx={{ flex: 1 }}
                        />
                    </SettingField>

                    {values.EST_PAYMENT_TYPE === "insurance" && (
                        <SettingField label="Overhead" description="O&P overhead percent for insurance claims">
                            <Input
                                size="sm"
                                endDecorator="%"
                                type="number"
                                value={values.EST_OP_PERCENT}
                                onChange={(e) => onChange("EST_OP_PERCENT", e.target.value)}
                                sx={{ width: 100 }}
                            />
                        </SettingField>
                    )}

                    {(values.EST_PAYMENT_TYPE === "credit" || values.EST_PAYMENT_TYPE === "debit") && (
                        <SettingField label="Card processing fee">
                            <Input
                                size="sm"
                                endDecorator="%"
                                value={values.EST_CC_FEE_PERCENT}
                                type="number"
                                onChange={(e) => onChange("EST_CC_FEE_PERCENT", e.target.value)}
                                sx={{ width: 100 }}
                            />
                        </SettingField>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <SettingField label="Sales tax rate" description="Applied to material totals (Excel ~7.5%)">
                        <Input
                            size="sm"
                            endDecorator="%"
                            type="number"
                            value={values.EST_SALES_TAX_RATE}
                            onChange={(e) => onChange("EST_SALES_TAX_RATE", e.target.value)}
                            sx={{ width: 100 }}
                        />
                    </SettingField>

                    <SettingField label="General insurance / overhead" description="Added to contract price (Excel 3.25%)">
                        <Input
                            size="sm"
                            endDecorator="%"
                            type="number"
                            value={values.EST_OVERHEAD_PERCENT}
                            onChange={(e) => onChange("EST_OVERHEAD_PERCENT", e.target.value)}
                            sx={{ width: 100 }}
                        />
                    </SettingField>

                    <SettingField label="Referral fee">
                        <Input
                            size="sm"
                            startDecorator="$"
                            type="number"
                            value={values.EST_REFERRAL_FEE}
                            onChange={(e) => onChange("EST_REFERRAL_FEE", e.target.value)}
                            sx={{ width: 100 }}
                        />
                    </SettingField>

                    <SettingField label="Salesman commission %" description="Reference for margin planning">
                        <Input
                            size="sm"
                            endDecorator="%"
                            type="number"
                            value={values.EST_SALESMAN_COMMISSION_PERCENT}
                            onChange={(e) => onChange("EST_SALESMAN_COMMISSION_PERCENT", e.target.value)}
                            sx={{ width: 100 }}
                        />
                    </SettingField>
                </Box>
            </CardContent>
        </Card>
    );
}
